import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Search, Headphones, MessageSquare, Clock, CheckCircle, AlertCircle, Send, Sparkles, ArrowLeft
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DistributorSupportPage = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [formData, setFormData] = useState({ subject: '', message: '' });

  const fetchTickets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/support`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);


  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/support`, formData);
      toast.success('Ticket creado exitosamente');
      setShowCreate(false);
      setFormData({ subject: '', message: '' });
      fetchTickets();
    } catch (error) {
      toast.error('Error al crear ticket');
    }
  };

  const statusConfig = {
    open: { bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-400', label: 'Abierto' },
    in_progress: { bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-400', label: 'En Progreso' },
    resolved: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-400', label: 'Resuelto' },
    closed: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400', label: 'Cerrado' },
  };

  const filteredTickets = tickets.filter(t =>
    t.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-amber-100 border-t-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in" data-testid="distributor-support-page">
      {/* Header */}
      <div className="flex items-start justify-between pt-1">
        <div>
          <button
            onClick={() => navigate('/distributor/help')}
            className="flex items-center gap-1.5 text-sm text-amber-500 font-semibold mb-3 hover:gap-2.5 transition-all duration-200"
            data-testid="back-to-help"
          >
            <ArrowLeft size={16} /> Volver a Ayuda
          </button>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Soporte
          </h1>
          <p className="text-sm text-gray-400 mt-1 font-medium">Mis tickets de soporte</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:-translate-y-0.5 transition-all duration-300"
          data-testid="create-ticket-btn"
        >
          <Plus size={16} />
          Nuevo
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <Input
          placeholder="Buscar ticket..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-11 bg-white border-gray-100 focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20 rounded-xl h-11 shadow-sm"
          data-testid="search-tickets"
        />
      </div>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Headphones size={32} className="text-amber-300" />
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin tickets</p>
          <p className="text-sm text-gray-400 mt-1">Crea tu primer ticket de soporte</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-5 flex items-center gap-2 mx-auto bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-amber-500/25"
          >
            <Sparkles size={16} />
            Crear mi primer ticket
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((ticket, idx) => {
            const status = statusConfig[ticket.status] || statusConfig.open;
            return (
              <div
                key={ticket.id}
                className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                style={{ animationDelay: `${idx * 60}ms` }}
                onClick={() => { setSelectedTicket(ticket); setShowDetails(true); }}
                data-testid={`ticket-card-${ticket.id}`}
              >
                <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Headphones size={14} className="text-amber-500 flex-shrink-0" />
                        <span className="font-bold text-gray-900 text-sm truncate">{ticket.subject}</span>
                      </div>
                      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{ticket.message}</p>
                      <div className="flex items-center gap-3 mt-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${status.bg} ${status.text}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                          {status.label}
                        </span>
                        <span className="text-[10px] text-gray-400 font-medium">
                          {new Date(ticket.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                        </span>
                        {ticket.response && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                            <MessageSquare size={9} /> Respondido
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white border-0 rounded-3xl max-w-md" data-testid="create-ticket-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Nuevo Ticket
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold text-sm">Asunto *</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                placeholder="¿En qué podemos ayudarte?"
                className="bg-gray-50 border-transparent focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20 rounded-xl"
                data-testid="input-ticket-subject"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold text-sm">Mensaje *</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={5}
                placeholder="Describe tu problema o consulta..."
                className="bg-gray-50 border-transparent focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20 rounded-xl"
                data-testid="input-ticket-message"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}
                className="border-gray-200 text-gray-600 rounded-full">
                Cancelar
              </Button>
              <Button type="submit"
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full font-bold shadow-lg shadow-amber-500/25"
                data-testid="send-ticket-btn"
              >
                <Send size={16} className="mr-1.5" />
                Enviar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-white border-0 rounded-3xl max-w-md" data-testid="ticket-details-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Ticket de Soporte
            </DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-gray-900">{selectedTicket.subject}</span>
                  {(() => {
                    const s = statusConfig[selectedTicket.status] || statusConfig.open;
                    return (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${s.bg} ${s.text}`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                        {s.label}
                      </span>
                    );
                  })()}
                </div>
                <p className="text-xs text-gray-500">
                  {new Date(selectedTicket.created_at).toLocaleString('es-ES')}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold text-sm">Mi Mensaje</Label>
                <div className="p-4 bg-gray-50 border border-gray-100 rounded-xl">
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedTicket.message}</p>
                </div>
              </div>

              {selectedTicket.response ? (
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold text-sm flex items-center gap-2">
                    <MessageSquare size={14} className="text-emerald-500" />
                    Respuesta del Soporte
                  </Label>
                  <div className="p-4 bg-emerald-50 border border-emerald-100/50 rounded-xl">
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedTicket.response}</p>
                  </div>
                </div>
              ) : (
                <div className="p-5 rounded-xl text-center bg-amber-50 border border-amber-100/50">
                  <Clock className="mx-auto mb-2 text-amber-500" size={24} />
                  <p className="text-amber-700 text-sm font-medium">Esperando respuesta del equipo</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setShowDetails(false)}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-bold w-full shadow-lg shadow-amber-500/25"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DistributorSupportPage;
